package fr.frux.feature.map

import fr.frux.core.maps.Location
import fr.frux.core.maps.MapBounds
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.distanceMetersFrom
import kotlin.time.Duration.Companion.minutes

/*
 * Politique de rechargement et de cadrage de la camera : quand le lot charge ne couvre plus ce que
 * l'utilisateur regarde, et jusqu'a quand un lot reste frais. Regroupe la logique la plus subtile
 * du ViewModel (#186) : les seuils d'armement de la pastille « Rechercher dans cette zone » et la
 * peremption du lot affiche.
 */

/**
 * Ce que la camera montre : autour de quoi, jusqu'ou, et dans quel rectangle (#621, `null` tant
 * que l'hote ne le mesure pas : les seuils d'armement ne s'appuient que sur le rayon).
 */
internal data class CameraView(
    val center: Location,
    val radiusMeters: Double,
    val bounds: MapBounds? = null,
)

/**
 * Echelle du lot affiche : le rayon interroge (reference de tous les seuils d'armement) et le
 * rectangle visible envoye avec lui (#621, `null` pour les chargements au disque seul). Les
 * rechargements de la meme vue la reprennent telle quelle.
 */
internal data class LoadedView(
    val radiusMeters: Double,
    val bounds: MapBounds?,
) {
    companion object {
        fun initial() = LoadedView(DEFAULT_RADIUS_KM * 1_000, bounds = null)
    }
}

/**
 * Fraction du rayon charge au-dela de laquelle un deplacement justifie une nouvelle recherche.
 * A 5 km de rayon, cela fait 1,25 km : en deca, le lot deja affiche couvre encore ce que
 * l'utilisateur regarde, et lui proposer de rechercher ne changerait rien a l'ecran.
 */
internal const val CAMERA_RELOAD_RADIUS_FRACTION = 0.25

/**
 * Dezoom a partir duquel le lot charge ne couvre plus la vue. Sous ce facteur, les bords non
 * couverts restent marginaux au regard des 20 % de marge du disque circonscrit.
 */
internal const val ZOOM_OUT_ARM_FACTOR = 1.5

/**
 * Zoom avant a partir duquel une recherche plus serree a un sens — **et seulement sur un lot
 * tronque**. Zoomer dans un lot complet ne peut rien reveler : tous les arbres du rayon sont deja
 * a l'ecran.
 */
internal const val ZOOM_IN_ARM_FACTOR = 0.66

/**
 * En deca de cette diagonale, un rectangle a cadrer est degenere (un seul arbre, ou plusieurs
 * confondus) : le cadrer demanderait un zoom quasi infini. On vise alors son centre.
 */
internal const val MIN_FRAMEABLE_SPAN_METERS = 200.0

/** Zoom retenu quand il n'y a qu'un point a montrer. */
internal const val SINGLE_POINT_ZOOM = 15f

/**
 * Peremption du lot d'arbres affiche. Le ViewModel etant permanent (surface carte persistante,
 * #124), plus aucun cycle de vie ne rafraichit les donnees : au retour sur l'ecran carte, un lot
 * plus vieux que ce delai est recharge silencieusement.
 */
internal val TREES_STALE_AFTER = 5.minutes

/**
 * Le lot charge couvre-t-il encore ce que l'utilisateur regarde — arme la pastille « Rechercher
 * dans cette zone ». L'ecart se mesure depuis le centre interroge, pas la position precedente de
 * la camera (voir [MapViewModel.observeCameraMoves]).
 */
internal fun shouldArmAreaSearch(
    view: CameraView,
    queryCenter: Location,
    loaded: LoadedView,
    lastBatchTruncated: Boolean,
): Boolean {
    // Vue pas encore mesuree : aucune conclusion possible.
    if (view.radiusMeters <= 0.0) return false
    val loadedRadiusMeters = loaded.radiusMeters

    val movedFar =
        view.center.distanceMetersTo(queryCenter) >=
            loadedRadiusMeters * CAMERA_RELOAD_RADIUS_FRACTION
    val zoomedOut = view.radiusMeters > loadedRadiusMeters * ZOOM_OUT_ARM_FACTOR
    val zoomedIn =
        lastBatchTruncated &&
            view.radiusMeters < loadedRadiusMeters * ZOOM_IN_ARM_FACTOR

    return movedFar || zoomedOut || zoomedIn
}

/**
 * Ecran vide sous un filtre — serveur (saison, partie, forme) ou selection d'espece cote client :
 * proposer d'elargir. Au rayon plafond, il n'y a plus rien a elargir — le constat devient « rien
 * nulle part », sans bouton. Mesure sur les arbres visibles, pas le lot brut : la selection
 * d'espece retire des marqueurs sans requete.
 */
internal fun filteredNoResultsFor(
    visibleTrees: List<Tree>,
    filter: MapFilter,
    selectedRollupId: Long?,
    radiusKm: Double,
): FilteredNoResults? =
    when {
        visibleTrees.isNotEmpty() -> null
        !filter.query.isActive && selectedRollupId == null -> null
        radiusKm >= MAX_QUERY_RADIUS_KM -> FilteredNoResults.ANYWHERE
        else -> FilteredNoResults.IN_AREA
    }

/**
 * Variante pour un changement de selection SANS chargement : le lot en place peut appartenir a une
 * autre requete (elargissement d'une autre espece), donc jamais de conclusion ANYWHERE ici — au
 * pire on repropose l'elargissement, qui tranchera.
 */
internal fun selectionNoResults(
    visibleTrees: List<Tree>,
    filter: MapFilter,
    selectedRollupId: Long?,
): FilteredNoResults? =
    when {
        visibleTrees.isNotEmpty() -> null
        !filter.query.isActive && selectedRollupId == null -> null
        else -> FilteredNoResults.IN_AREA
    }

/**
 * Zone interrogee sans aucun arbre et sans aucun filtre (#478) : la carte serait nue sans un mot.
 * Mesure sur le lot brut, que seul un filtre serveur ou une espece transmise au serveur peut
 * restreindre. Ces deux cas relevent de [filteredNoResultsFor], dont le bandeau passe avant. Meme
 * regle du rayon plafond : au-dela, plus rien a elargir.
 */
internal fun emptyAreaFor(
    batch: List<Tree>,
    requestedFilter: MapFilter,
    radiusKm: Double,
): FilteredNoResults? =
    when {
        batch.isNotEmpty() -> null
        requestedFilter.query.isActive || requestedFilter.rollupId != null -> null
        radiusKm >= MAX_QUERY_RADIUS_KM -> FilteredNoResults.ANYWHERE
        else -> FilteredNoResults.IN_AREA
    }

/** Restreint un lot a une agregation d'espece, comme [MapState.visibleTrees] mais hors etat. */
internal fun visibleUnderRollup(
    trees: List<Tree>,
    rollupId: Long?,
    speciesRollup: Map<Long, Long>,
): List<Tree> = rollupId?.let { r -> trees.filter { speciesRollup[it.species.id] == r } } ?: trees

/** Assemblage de [MapViewModel.seeAllContext], sorti de la classe. Meme clamp que le chargement. */
internal fun seeAllContextFrom(
    view: CameraView?,
    queryCenter: Location,
    loadedRadiusMeters: Double,
    userLocation: Location?,
): SeeAllContext {
    val center = view?.center ?: queryCenter
    val radiusKm =
        ((view?.radiusMeters ?: loadedRadiusMeters) / 1_000)
            .coerceIn(MIN_QUERY_RADIUS_KM, MAX_QUERY_RADIUS_KM)
    return SeeAllContext(
        latitude = center.latitude,
        longitude = center.longitude,
        radiusKm = radiusKm,
        userLatitude = userLocation?.latitude,
        userLongitude = userLocation?.longitude,
    )
}
